import { useEffect, useState } from 'preact/hooks';
import { BackLink } from '../components/BackLink';
import { PlaceholderScreen } from './PlaceholderScreen';
import {
  getProjectDetail,
  searchProjectEnrichment,
  applyProjectEnrichment,
  uploadCallSheet,
  uploadSides,
  uploadContract,
  setContractStatus,
  ApiError,
  type ProjectDetail as ProjectDetailData,
  type ProjectShootDay,
  type ProjectSides,
  type ProjectContract,
  type EnrichCandidate,
  type EnrichMedium,
  type RecordRow,
} from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { PHASE_PILL } from './projectPresentation';
import { PosterArt, PosterUpload } from '../components/PosterImage';
import { PersonAvatar } from '../components/Avatar';
import { RevisionChip, ChangeFlag, currentSides, priorSides } from './sidesPresentation';
import './ProjectDetail.css';

export interface ProjectDetailProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenAudition: (auditionId: string) => void;
  onViewSource: (sourceId: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';
type EnrichState = 'idle' | 'searching' | 'candidates' | 'applying' | 'error';

function fieldText(record: RecordRow, name: string): string | null {
  const value = record.fields[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

// actor_project.medium -> the enrichment search's narrower hint set
// (film/tv/voice); anything else is sent as no hint rather than a wrong one.
function mediumHint(medium: string | null): EnrichMedium | undefined {
  if (medium === 'film' || medium === 'tv') return medium;
  if (medium === 'new_media') return 'voice';
  return undefined;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// The shoot-date the parser captured is usually a human-readable string off the
// sheet ("Sat. November 20th, 2021") rather than an ISO date, so show it as-is;
// only fall through to formatDate if it happens to be ISO-parseable.
function displayShootDate(raw: string | null): string | null {
  if (!raw) return null;
  return formatDate(raw) ?? raw;
}

// Call times arrive normalized to 24h HHMM ("1300"); render them as a friendly
// clock ("1:00 pm"). A non-time cell ("TBD", "As req'd") passes through verbatim.
function formatClock(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = /^(\d{1,2}):?(\d{2})$/.exec(raw.trim());
  if (!m) return raw;
  let hour = Number.parseInt(m[1]!, 10);
  const minute = m[2]!;
  if (hour > 23 || Number.parseInt(minute, 10) > 59) return raw;
  const suffix = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour}:${minute} ${suffix}`;
}

interface Scene {
  number?: string;
  set?: string;
  description?: string;
  castIds?: string[];
  pages?: string;
  dayNight?: string;
}

// The shoot day stores its scenes as a JSON string of Scene[] (B6a). Parse it
// back defensively — a malformed/absent value yields an empty list, never a
// throw.
function parseScenes(raw: string | null): Scene[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Scene[]) : [];
  } catch {
    return [];
  }
}

// The character a worked-with person played was stored by the call-sheet ingest
// in person.notes as "Cast: <character>" (B6a). Pull it back out for display.
function castCharacter(person: RecordRow): string | null {
  const notes = fieldText(person, 'notes');
  if (!notes) return null;
  const m = /^cast:\s*(.+)$/i.exec(notes.trim());
  return m ? m[1]!.trim() : null;
}

// A single source chip (the `.srclink` from the demo) — opens the untouched
// source viewer.
function SourceChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" class="srclink" onClick={onClick}>
      <span class="srclink__pin" aria-hidden="true">
        📄
      </span>
      {label}
    </button>
  );
}

// The phase-adaptive project detail. The lifecycle_state (auditioned/booked/
// released) drives which body renders; every section shows only from real
// composed-detail + linked-source data (nothing fabricated — absent data
// shows a calm empty, never invented specifics).
export function ProjectDetail({ tenantId, id, onBack, onOpenAudition, onViewSource }: ProjectDetailProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [detail, setDetail] = useState<ProjectDetailData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [enrichState, setEnrichState] = useState<EnrichState>('idle');
  const [candidates, setCandidates] = useState<EnrichCandidate[]>([]);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  // R3 — reload the composed detail after an edit/act on this (or a child) record.
  const recordsVersion = useRecordsVersion();

  async function load() {
    setState('loading');
    try {
      const data = await getProjectDetail(tenantId, id);
      setDetail(data);
      setState('ready');
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
      setState('error');
    }
  }

  useEffect(() => {
    void load();
    // reset the enrichment sub-flow when the project changes
    setEnrichState('idle');
    setCandidates([]);
    setEnrichError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, recordsVersion]);

  async function findMatch(project: RecordRow) {
    const title = fieldText(project, 'title') ?? project.display_name;
    setEnrichState('searching');
    setEnrichError(null);
    try {
      const year = fieldText(project, 'air_date') ?? fieldText(project, 'season') ?? undefined;
      const hint = mediumHint(fieldText(project, 'medium'));
      const results = await searchProjectEnrichment(tenantId, id, {
        title,
        ...(hint ? { medium: hint } : {}),
        ...(year ? { year } : {}),
      });
      setCandidates(results);
      setEnrichState('candidates');
    } catch (err) {
      setEnrichError(err instanceof ApiError ? err.message : 'Could not reach the enrichment service.');
      setEnrichState('error');
    }
  }

  // The propose -> APPROVE moment: only fires on an explicit tap of one
  // candidate. Never auto-applies (there is no path here that applies without
  // this call).
  async function confirmMatch(qid: string) {
    setEnrichState('applying');
    setEnrichError(null);
    try {
      await applyProjectEnrichment(tenantId, id, qid);
      setCandidates([]);
      setEnrichState('idle');
      await load();
    } catch (err) {
      setEnrichError(err instanceof ApiError ? err.message : 'Could not apply this match.');
      setEnrichState('error');
    }
  }

  if (state === 'loading') {
    return (
      <div class="project-detail">
        <BackLink label="Projects" onClick={onBack} />
        <PlaceholderScreen eyebrow="Project" line="One moment…" />
      </div>
    );
  }

  if (state === 'error' || !detail) {
    return (
      <div class="project-detail">
        <BackLink label="Projects" onClick={onBack} />
        <PlaceholderScreen eyebrow="Project" line="Couldn't load this project." soft={errorMessage ?? undefined} />
      </div>
    );
  }

  const { project, auditions, people } = detail;
  const shootDays = detail.shootDays ?? [];
  const workedWith = detail.workedWith ?? [];
  const sides = detail.sides ?? [];
  const contracts = detail.contracts ?? [];
  const title = fieldText(project, 'title') ?? project.display_name;
  const role = fieldText(project, 'role');
  const studio = fieldText(project, 'production_company');
  const notes = fieldText(project, 'notes');
  const medium = fieldText(project, 'medium');
  const year = fieldText(project, 'air_date') ?? fieldText(project, 'season');
  const phase = project.lifecycle_state ?? 'auditioned';
  const pill = PHASE_PILL[phase] ?? { label: phase, cls: 'aud' as const };

  const poster = detail.poster ?? null;
  const confirmed = fieldText(project, 'enrichment_confirmed_at');
  const synopsis = fieldText(project, 'synopsis');
  const airDate = fieldText(project, 'air_date');
  const genres = fieldText(project, 'genres');
  const imdbId = fieldText(project, 'imdb_id');

  const subParts = [role, studio].filter(Boolean).join(' · ');

  return (
    <div class="project-detail">
      <BackLink label="Projects" onClick={onBack} />

      <div class="project-detail__hero poster">
        <PosterArt tenantId={tenantId} mediaId={poster?.id} medium={medium} year={year} alt={`${title} poster`} tall />
      </div>
      <PosterUpload tenantId={tenantId} projectId={id} hasPoster={poster !== null} onDone={() => void load()} />
      <div class="project-detail__title">{title}</div>
      {subParts && <div class="project-detail__sub">{subParts}</div>}
      <div class="project-detail__pill-row">
        <span class={`ph ph--${pill.cls}`}>{pill.label}</span>
        <button
          type="button"
          class="detail-edit-btn"
          onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'actor_project', id })}
          data-testid="edit-project"
        >
          Edit
        </button>
      </div>
      {notes && <div class="project-detail__note">{notes}</div>}

      {/* ---- Enrichment: confirm-a-match (propose -> approve) ---- */}
      <EnrichmentSection
        confirmed={confirmed}
        synopsis={synopsis}
        airDate={airDate}
        genres={genres}
        imdbId={imdbId}
        enrichState={enrichState}
        candidates={candidates}
        enrichError={enrichError}
        onFindMatch={() => void findMatch(project)}
        onConfirm={(qid) => void confirmMatch(qid)}
        onCancel={() => {
          setEnrichState('idle');
          setCandidates([]);
          setEnrichError(null);
        }}
      />

      {/* ---- Phase-adaptive body ---- */}
      {phase === 'booked' && (
        <BookedBody
          tenantId={tenantId}
          projectId={id}
          shootDays={shootDays}
          sides={sides}
          contracts={contracts}
          onViewSource={onViewSource}
          onReload={() => void load()}
        />
      )}
      {phase === 'released' && <ReleasedBody />}
      {phase === 'auditioned' && <AuditionedBody auditions={auditions} onOpenAudition={onOpenAudition} />}

      {/* ---- Worked with (from the call sheet) ---- */}
      {workedWith.length > 0 && (
        <>
          <div class="project-detail__grp">Worked with · from the call sheet</div>
          {workedWith.map((person) => {
            const character = castCharacter(person);
            const name = fieldText(person, 'full_name') ?? person.display_name;
            return (
              <div key={person.id} class="project-detail__person">
                <PersonAvatar tenantId={tenantId} personId={person.id} name={name} size="sm" />
                <div class="project-detail__person-body">
                  <div class="project-detail__person-name">{name}</div>
                  {character && <div class="project-detail__person-role">as {character}</div>}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ---- People involved (shared) ---- */}
      {people.length > 0 && (
        <>
          <div class="project-detail__grp">People involved</div>
          {people.map((person) => {
            const name = fieldText(person, 'full_name') ?? person.display_name;
            return (
              <div key={person.id} class="project-detail__person">
                <PersonAvatar tenantId={tenantId} personId={person.id} name={name} size="sm" />
                <div class="project-detail__person-body">
                  <div class="project-detail__person-name">{name}</div>
                  {fieldText(person, 'person_role') && (
                    <div class="project-detail__person-role">{fieldText(person, 'person_role')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

interface EnrichmentSectionProps {
  confirmed: string | null;
  synopsis: string | null;
  airDate: string | null;
  genres: string | null;
  imdbId: string | null;
  enrichState: EnrichState;
  candidates: EnrichCandidate[];
  enrichError: string | null;
  onFindMatch: () => void;
  onConfirm: (qid: string) => void;
  onCancel: () => void;
}

function EnrichmentSection({
  confirmed,
  synopsis,
  airDate,
  genres,
  imdbId,
  enrichState,
  candidates,
  enrichError,
  onFindMatch,
  onConfirm,
  onCancel,
}: EnrichmentSectionProps) {
  // Already confirmed -> the enriched metadata + the attribution line.
  if (confirmed) {
    return (
      <div class="enrich">
        <div class="project-detail__grp">Metadata</div>
        {synopsis && <div class="enrich__synopsis">{synopsis}</div>}
        {airDate && (
          <div class="project-detail__kvline">
            <span class="project-detail__kvline-k">Air date</span>
            <span class="project-detail__kvline-v">{airDate}</span>
          </div>
        )}
        {genres && (
          <div class="project-detail__kvline">
            <span class="project-detail__kvline-k">Genres</span>
            <span class="project-detail__kvline-v">{genres}</span>
          </div>
        )}
        {imdbId && (
          <div class="project-detail__kvline">
            <span class="project-detail__kvline-k">IMDb</span>
            <a
              class="project-detail__kvline-v enrich__imdb"
              href={`https://www.imdb.com/title/${imdbId}/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {imdbId} ›
            </a>
          </div>
        )}
        <div class="enrich__attribution">Enriched from Wikidata · confirmed by you</div>
      </div>
    );
  }

  // Showing ranked candidates to confirm.
  if (enrichState === 'candidates') {
    return (
      <div class="enrich">
        <div class="project-detail__grp">Confirm the match</div>
        <div class="enrich__hint">Nothing is saved until you pick one. Tap the one that's this project.</div>
        {candidates.length === 0 ? (
          <div class="enrich__empty">No matches found. You can leave this un-enriched.</div>
        ) : (
          candidates.map((candidate, index) => (
            <button
              type="button"
              key={candidate.qid}
              class="enrich-candidate"
              onClick={() => onConfirm(candidate.qid)}
            >
              <div class="enrich-candidate__top">
                <span class="enrich-candidate__label">{candidate.label ?? candidate.qid}</span>
                {index === 0 && <span class="enrich-candidate__best">Best match</span>}
              </div>
              {candidate.description && <div class="enrich-candidate__desc">{candidate.description}</div>}
              <div class="enrich-candidate__meta">
                {[candidate.type, candidate.year].filter(Boolean).join(' · ')}
              </div>
              <div class="enrich-candidate__confirm">Confirm this match ›</div>
            </button>
          ))
        )}
        {enrichError && <div class="enrich__error">{enrichError}</div>}
        <button type="button" class="enrich__cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    );
  }

  // Idle / searching / applying / error -> the "Find match" affordance.
  const busy = enrichState === 'searching' || enrichState === 'applying';
  return (
    <div class="enrich">
      <button type="button" class="enrich__find" onClick={onFindMatch} disabled={busy}>
        {enrichState === 'searching' ? 'Finding matches…' : enrichState === 'applying' ? 'Saving…' : 'Find match'}
      </button>
      <div class="enrich__find-note">Look this up on Wikidata — you confirm before anything is saved.</div>
      {enrichError && <div class="enrich__error">{enrichError}</div>}
    </div>
  );
}

interface BookedBodyProps {
  tenantId: string;
  projectId: string;
  shootDays: ProjectShootDay[];
  sides: ProjectSides[];
  contracts: ProjectContract[];
  onViewSource: (id: string) => void;
  onReload: () => void;
}

// Orders shoot days most-recent-first (by shoot_date, then day_number) so the
// upcoming/latest day is what greets you; ties fall back to insertion order.
function orderShootDays(days: ProjectShootDay[]): ProjectShootDay[] {
  return [...days].sort((a, b) => {
    const da = fieldText(a, 'shoot_date') ?? '';
    const db = fieldText(b, 'shoot_date') ?? '';
    if (da !== db) return db.localeCompare(da);
    const na = typeof a.fields['day_number'] === 'number' ? (a.fields['day_number'] as number) : 0;
    const nb = typeof b.fields['day_number'] === 'number' ? (b.fields['day_number'] as number) : 0;
    return nb - na;
  });
}

function BookedBody({ tenantId, projectId, shootDays, sides, contracts, onViewSource, onReload }: BookedBodyProps) {
  const ordered = orderShootDays(shootDays);
  const [primary, ...rest] = ordered;

  return (
    <>
      <div class="project-detail__grp">Day of · from the call sheet</div>
      {primary ? (
        <>
          <ShootDayCard day={primary} onViewSource={onViewSource} />
          {rest.length > 0 && (
            <details class="daycard-more">
              <summary class="daycard-more__summary">
                {rest.length === 1 ? 'One more shoot day' : `${rest.length} more shoot days`}
              </summary>
              {rest.map((day) => (
                <ShootDayCard key={day.id} day={day} onViewSource={onViewSource} />
              ))}
            </details>
          )}
        </>
      ) : (
        <div class="project-detail__empty">No call sheet in yet — add one below and your day-of appears here.</div>
      )}

      <AddCallSheet tenantId={tenantId} projectId={projectId} onDone={onReload} />

      <div class="project-detail__grp">Sides</div>
      <SidesSection sides={sides} onViewSource={onViewSource} />
      <AddSides tenantId={tenantId} projectId={projectId} onDone={onReload} />

      <div class="project-detail__grp">Contracts</div>
      <ContractsSection
        tenantId={tenantId}
        contracts={contracts}
        onViewSource={onViewSource}
        onReload={onReload}
      />
      <AddContract tenantId={tenantId} projectId={projectId} onDone={onReload} />
    </>
  );
}

// The SIDES block: the current revision shown prominently (colour chip +
// version label + pages/scenes affected + a CALM "new revision" flag when it
// supersedes a prior), with the prior revisions folded into a collapsible
// history. Each chip opens that revision's ORIGINAL file.
function SidesSection({ sides, onViewSource }: { sides: ProjectSides[]; onViewSource: (id: string) => void }) {
  const current = currentSides(sides);
  if (!current) {
    return <div class="project-detail__empty">Sides will appear here once they're in — add the first revision below.</div>;
  }
  const prior = priorSides(sides, current);
  const pages = fieldText(current, 'pages_or_scenes_affected');
  const changeSummary = fieldText(current, 'change_summary');
  const supersedesPrior = current.supersedesId !== null;

  return (
    <div class="sides-current">
      <div class="sides-current__top">
        <RevisionChip sides={current} onViewSource={onViewSource} />
        {supersedesPrior && <ChangeFlag sides={current} />}
      </div>
      {pages && <div class="sides-current__pages">Pages / scenes: {pages}</div>}
      {changeSummary && <div class="sides-current__summary">{changeSummary}</div>}

      {prior.length > 0 && (
        <details class="sides-history">
          <summary class="sides-history__summary">
            {prior.length === 1 ? 'One earlier revision' : `${prior.length} earlier revisions`}
          </summary>
          {prior.map((rev) => (
            <div key={rev.id} class="sides-history__row">
              <RevisionChip sides={rev} onViewSource={onViewSource} />
              {fieldText(rev, 'pages_or_scenes_affected') && (
                <span class="sides-history__pages">{fieldText(rev, 'pages_or_scenes_affected')}</span>
              )}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

const CONTRACT_KIND_LABEL: Record<string, string> = {
  deal_memo: 'Deal memo',
  engagement: 'Engagement',
  nda: 'NDA',
  amendment: 'Amendment',
  release: 'Release',
  other: 'Contract',
};

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  received: 'Received',
  under_review: 'Under review',
  signed: 'Signed',
  countersigned: 'Countersigned',
  declined: 'Declined',
};

// The guarded lifecycle's legal next steps, mirrored from the actor pack's
// transitions (received -> under_review -> signed -> countersigned; declined
// reachable from received|under_review). The server is the source of truth —
// an illegal transition still 422s — this map only decides which calm controls
// to OFFER.
const CONTRACT_NEXT: Record<string, string[]> = {
  received: ['under_review', 'declined'],
  under_review: ['signed', 'declined'],
  signed: ['countersigned'],
  countersigned: [],
  declined: [],
};

// The CONTRACTS block: each contract with its kind, a status badge, a calm
// "response due" note when respond_by is set, inline status controls that call
// the guarded endpoint, and a chip to open its ORIGINAL file.
function ContractsSection({
  tenantId,
  contracts,
  onViewSource,
  onReload,
}: {
  tenantId: string;
  contracts: ProjectContract[];
  onViewSource: (id: string) => void;
  onReload: () => void;
}) {
  if (contracts.length === 0) {
    return <div class="project-detail__empty">Deal memo and contracts will appear here once they're in.</div>;
  }
  return (
    <>
      {contracts.map((contract) => (
        <ContractCard
          key={contract.id}
          tenantId={tenantId}
          contract={contract}
          onViewSource={onViewSource}
          onReload={onReload}
        />
      ))}
    </>
  );
}

function ContractCard({
  tenantId,
  contract,
  onViewSource,
  onReload,
}: {
  tenantId: string;
  contract: ProjectContract;
  onViewSource: (id: string) => void;
  onReload: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kind = fieldText(contract, 'kind');
  const kindLabel = (kind && CONTRACT_KIND_LABEL[kind]) ?? 'Contract';
  const status = contract.lifecycle_state ?? 'received';
  const statusLabel = CONTRACT_STATUS_LABEL[status] ?? status;
  const rate = fieldText(contract, 'rate');
  const respondBy = formatDate(fieldText(contract, 'respond_by'));
  const next = CONTRACT_NEXT[status] ?? [];

  async function transition(to: string) {
    setBusy(true);
    setError(null);
    try {
      await setContractStatus(tenantId, contract.id, to);
      onReload();
    } catch (err) {
      // Illegal transitions come back 422 — surface calmly, never an alarm.
      setError(err instanceof ApiError ? err.message : 'Could not update the status.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="contract-card">
      <div class="contract-card__top">
        <span class="contract-card__kind">{kindLabel}</span>
        <span class={`contract-badge contract-badge--${status}`}>{statusLabel}</span>
      </div>
      {rate && <div class="contract-card__rate">{rate}</div>}
      {respondBy && <div class="contract-card__due">Response due {respondBy}</div>}

      {next.length > 0 && (
        <div class="contract-card__actions">
          {next.map((to) => (
            <button
              type="button"
              key={to}
              class="contract-card__act"
              disabled={busy}
              onClick={() => void transition(to)}
            >
              {CONTRACT_STATUS_LABEL[to] ?? to}
            </button>
          ))}
        </div>
      )}
      {error && <div class="contract-card__error">{error}</div>}

      {contract.sourceId && (
        <div class="contract-card__src">
          <SourceChip label="Source · original" onClick={() => onViewSource(contract.sourceId!)} />
        </div>
      )}
    </div>
  );
}

// Calm "Add sides" upload — mirrors AddCallSheet. Multipart POST /api/actor/
// sides anchored to this project; reloads the detail so the new revision (and,
// if it superseded a prior, the change flag) appears.
function AddSides({ tenantId, projectId, onDone }: { tenantId: string; projectId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadSides(tenantId, { projectId }, file);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the sides.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="add-callsheet">
      <label class="add-callsheet__btn">
        <input type="file" class="add-callsheet__input" onChange={(e) => void onPick(e)} disabled={busy} />
        {busy ? 'Adding…' : '+ Add sides'}
      </label>
      {error && <div class="add-callsheet__error">{error}</div>}
    </div>
  );
}

// Calm "Add contract" upload — mirrors AddCallSheet. Multipart POST /api/actor/
// contracts anchored to this project; reloads the detail after.
function AddContract({ tenantId, projectId, onDone }: { tenantId: string; projectId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadContract(tenantId, projectId, file);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the contract.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="add-callsheet">
      <label class="add-callsheet__btn">
        <input type="file" class="add-callsheet__input" onChange={(e) => void onPick(e)} disabled={busy} />
        {busy ? 'Adding…' : '+ Add contract'}
      </label>
      {error && <div class="add-callsheet__error">{error}</div>}
    </div>
  );
}

// The inline day-of, mirroring the demo's `.daycard`: day N of M + weather
// header, the call-time grid (general call + the subject's own call/HMU/on-set/
// wrap), location + base camp, and the scene list — all from the real parsed
// shoot day (nothing fabricated; a field with no value simply doesn't render).
function ShootDayCard({ day, onViewSource }: { day: ProjectShootDay; onViewSource: (id: string) => void }) {
  const dayNumber = typeof day.fields['day_number'] === 'number' ? (day.fields['day_number'] as number) : null;
  const dayTotal = typeof day.fields['day_total'] === 'number' ? (day.fields['day_total'] as number) : null;
  const dayLabel = dayNumber !== null ? `Day ${dayNumber}${dayTotal !== null ? ` of ${dayTotal}` : ''}` : null;
  const shootDate = displayShootDate(fieldText(day, 'shoot_date'));
  const headerLeft = [dayLabel, shootDate].filter(Boolean).join(' · ') || 'Shoot day';
  const weather = fieldText(day, 'weather');

  const location = fieldText(day, 'location');
  const baseCamp = fieldText(day, 'base_camp');
  const locLine = [location, baseCamp ? `Base camp: ${baseCamp}` : null].filter(Boolean).join(' · ');
  const character = fieldText(day, 'subject_character');

  const calls: Array<[string, string | null]> = [
    ['General call', formatClock(fieldText(day, 'general_call'))],
    ['Your call', formatClock(fieldText(day, 'subject_call'))],
    ['Hair / makeup', formatClock(fieldText(day, 'subject_hmu'))],
    ['On set', formatClock(fieldText(day, 'subject_on_set'))],
    ['Wrap (est.)', formatClock(fieldText(day, 'wrap_estimate'))],
  ];
  const shownCalls = calls.filter(([, v]) => v);
  const scenes = parseScenes(fieldText(day, 'scenes'));

  return (
    <div class="daycard">
      <div class="daycard__row">
        <span class="daycard__l">{headerLeft}</span>
        {weather && <span class="daycard__r">{weather}</span>}
      </div>
      {locLine && <div class="daycard__loc">{locLine}</div>}
      {character && <div class="daycard__role">You're on as {character}</div>}

      {shownCalls.length > 0 && (
        <div class="daycard__times">
          {shownCalls.map(([label, value]) => (
            <div key={label} class="daycard__dt">
              <span>{label}</span>
              <b>{value}</b>
            </div>
          ))}
        </div>
      )}

      {scenes.length > 0 && (
        <>
          <div class="daycard__scenes-label">Scenes</div>
          {scenes.map((scene, i) => {
            const head = [scene.number ? `Sc ${scene.number}` : null, scene.set].filter(Boolean).join(' — ');
            const tail = [
              scene.pages ? `${scene.pages} pp` : null,
              scene.dayNight,
              scene.castIds && scene.castIds.length ? `cast ${scene.castIds.join(', ')}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div key={`${scene.number ?? 'sc'}-${i}`} class="daycard__scene">
                <span class="daycard__scene-head">{head || 'Scene'}</span>
                {tail && <span class="daycard__scene-tail">{tail}</span>}
              </div>
            );
          })}
        </>
      )}

      {day.sourceId && (
        <div class="daycard__src">
          <SourceChip label="Source · call sheet" onClick={() => onViewSource(day.sourceId!)} />
        </div>
      )}
    </div>
  );
}

// The calm "Add call sheet" affordance: a single control that opens the file
// picker, uploads the ORIGINAL to POST /api/actor/call-sheets, then asks the
// detail to reload so the new day-of appears. Not a loud form.
function AddCallSheet({
  tenantId,
  projectId,
  onDone,
}: {
  tenantId: string;
  projectId: string;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = ''; // allow re-picking the same file later
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadCallSheet(tenantId, projectId, file);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the call sheet.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="add-callsheet">
      <label class="add-callsheet__btn">
        <input type="file" class="add-callsheet__input" onChange={(e) => void onPick(e)} disabled={busy} />
        {busy ? 'Adding…' : '+ Add call sheet'}
      </label>
      {error && <div class="add-callsheet__error">{error}</div>}
    </div>
  );
}

function ReleasedBody() {
  return (
    <>
      <div class="project-detail__grp">Where to watch</div>
      <div class="project-detail__stub">Streaming and rental links land here once this title is matched.</div>
      <div class="project-detail__grp">Accounting</div>
      <div class="project-detail__stub">Session pay and residuals for this credit will be tracked here.</div>
    </>
  );
}

function AuditionedBody({
  auditions,
  onOpenAudition,
}: {
  auditions: RecordRow[];
  onOpenAudition: (id: string) => void;
}) {
  return (
    <>
      <div class="project-detail__grp">Auditions for this project</div>
      {auditions.length === 0 ? (
        <div class="project-detail__empty">No auditions linked yet.</div>
      ) : (
        auditions.map((audition) => {
          const role = fieldText(audition, 'role');
          const stage = audition.lifecycle_state;
          return (
            <button
              type="button"
              key={audition.id}
              class="project-detail__aud"
              onClick={() => onOpenAudition(audition.id)}
            >
              <div class="project-detail__aud-name">
                {role ?? fieldText(audition, 'project') ?? audition.display_name}
              </div>
              <div class="project-detail__aud-sub">
                {[stage, 'media + sessions inside'].filter(Boolean).join(' · ')}
              </div>
            </button>
          );
        })
      )}
    </>
  );
}
